import { useCommunityContext } from 'components/CommunityContext';
import { useState } from 'react';
import * as filestack from 'filestack-js';
import { AWS_S3_URL } from 'utils/constants';
import { Attachment } from 'schema/Post/types';
import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';

export default function useAttachment(initValue: Attachment[]) {
  const { community, filestackSecurity } = useCommunityContext();
  const [isUploading, setIsUploading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>(initValue || []);

  const filestackClient = filestack.init(process.env.FILESTACK_API_KEY);

  const uploadFiles = async (
    files: File[],
    attachmentType: PostAttachmentType
  ) => {
    setIsUploading(true);

    // Create temporary preview attachments for the new files
    const selectedAttachments = files.map((file) => ({
      fileName: file.name,
      fileSizeInBytes: file.size,
      isLoading: true,
      url: URL.createObjectURL(file),
      attachmentType,
    }));

    // Capture the current attachments length before appending the new ones.
    // This is needed so we know where to splice in the final attachment objects.
    const startIndex = attachments.length;

    setAttachments((prev) => [...prev, ...selectedAttachments]);

    try {
      const storageOptions = {
        access: 'public',
        container: process.env.S3_IMPORT_BUCKET,
        location: 's3',
        path: community.s3UploadPath,
        region: process.env.AWS_REGION,
      };

      const uploadedFiles = await filestackClient.multiupload(
        files,
        {},
        storageOptions,
        null,
        filestackSecurity
      );

      const newAttachments = uploadedFiles.map((uploadedFile) => ({
        fileName: uploadedFile.filename,
        fileSizeInBytes: uploadedFile.size,
        filestackId: uploadedFile.handle,
        s3Key: uploadedFile.key,
        s3Bucket: uploadedFile.container,
        url: `https://${uploadedFile.container}.${AWS_S3_URL}/${uploadedFile.key}`,
        attachmentUrl: '', // only for video attachments
        attachmentType,
      }));

      // Replace the temporary attachments with the uploaded ones,
      // preserving any previous attachments.
      setAttachments((prev) => {
        const updated = [...prev];
        updated.splice(
          startIndex,
          selectedAttachments.length,
          ...newAttachments
        );
        return updated;
      });
      setIsUploading(false);
      return newAttachments;
    } catch (error) {
      setIsUploading(false);
      throw error;
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return {
    attachments,
    isUploading,
    uploadFiles,
    removeAttachment,
  };
}
